import { Component, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MemberClient, Person } from '@benefitsui/app/api/benefits-clients';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { Router } from '@angular/router';
import { PolicyTokensAccessor } from '@benefitsui/app/lib/policy/policy-token/policy-token-accessor.service';
import { PolicyService } from '@benefitsui/app/services/policy/policy.service';

enum STATES {
  INITIAL,
  NO_RESULTS,
  RESULTS_IN_USERS_GROUP,
  RESULTS_NOT_IN_USERS_GROUP,
}

@Component({
  selector: 'app-search-by-ssn-employee',
  templateUrl: './search-by-ssn.component.html',
  styleUrls: ['./search-by-ssn.component.scss'],
  standalone: false
})
export class SearchBySsnComponent {
  memberClient = inject(MemberClient);
  fb = inject(FormBuilder);
  router = inject(Router);
  policyTokenAccessor = inject(PolicyTokensAccessor);
  policyService = inject(PolicyService);

  STATES = STATES;
  state = STATES.INITIAL;
  loading = false;
  unexpectedErrorMsg = '';
  token = this.policyTokenAccessor.get().value.join(',');
  groupBekId = this.policyService.groupId;

  searchResult: Person | null = null;

  form = this.fb.group({
    searchTerm: ['', [Validators.required, Validators.pattern(/^\d{3}-\d{2}-\d{4}$/)]]
  });

  get searchTerm() {
    return this.form.get('searchTerm')?.value;
  }

  formatSearchTerm(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // Remove non-digits

    if (value.length > 3 && value.length <= 5) {
      value = value.slice(0, 3) + '-' + value.slice(3);
    } else if (value.length > 5) {
      value = value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5, 9);
    }

    input.value = value;
    this.form.get('searchTerm')?.setValue(value, { emitEvent: false }); // prevent loop
  }

  onCancelClick() {
    this.setState(STATES.INITIAL);
  }

  onAddEmployeeClick() {
    this.router.navigate(['/benefits/employees/add-form'], { 
      queryParams: { 
        token: this.token,
        ssn: this.searchTerm,
        memberId: this.searchResult?.personBek || ''
      }
    });
  }

  setUnexpectedError(err: string) {
    console.error(err ?? 'An unexpected error occurred');
    this.unexpectedErrorMsg = [
      'An unexpected error has occurred.',
      'Please try again, or',
      `<a href="/benefits/contacts?token=${this.token}">contact us</a>`,
      'if this issue persists.'
    ].join(' ');
  }

  onSearchKeypress(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this.onSearchClick();
    }
  }

  onSearchClick() {
    const searchTerm = this.form.get('searchTerm')?.value;
    if (!searchTerm) {
      return;
    }

    this.memberClient.lookup({
      searchTerm,
      limit: '20',
      offset: '0'
    }).pipe(
      catchError((err) => {
        console.log(`Encountered the following unexpected error: ${JSON.stringify(err)}`);
        return of('Error');
      })
    ).subscribe((result) => {
      if (typeof result === 'string') {
        this.setUnexpectedError(`API call for searchTerm failed unexpectedly - please check logs`);
        return;
      }

      const { data } = result;
      if (!data) {
        this.setUnexpectedError(`No error, but resulting data is undefined - please check logs`);
        return;
      }

      if (data.length <= 0) {
        this.setState(STATES.NO_RESULTS);
        return;
      }

      // while testing, I discovered that even though
      // the resulting people records should have a string
      // employer bek, sometimes they were coming back as a number.
      // gotta make sure we're comparing apples to apples
      const rawBek = data?.length > 0 ? data[0].employerBek : null;
      const personGroupBek = typeof (rawBek) === 'number' ? `${rawBek}` : null;
      let memberOfMyGroup = data.find(() => personGroupBek === this.groupBekId);
      let state;
      if (memberOfMyGroup) {
        state = STATES.RESULTS_IN_USERS_GROUP;
      } else {
        state = STATES.RESULTS_NOT_IN_USERS_GROUP;
        memberOfMyGroup = data[0];
      }

      this.searchResult = memberOfMyGroup;
      this.setState(state);
    });
  }

  setState(newState: STATES) {

    // use this switch statement to perform any state resets
    // before updating the page
    switch (newState) {
      case STATES.INITIAL:
        break;
      case STATES.NO_RESULTS:
        break;
      case STATES.RESULTS_IN_USERS_GROUP:
        break;
      case STATES.RESULTS_NOT_IN_USERS_GROUP:
        break;
    }

    this.unexpectedErrorMsg = '';
    this.state = newState;
  }

  getPersonUrl(person: Person | null) {
    if (person == null) {
      throw `Cannot generate a person url for a null person object`;
    }

    return `/benefits/employees/${person.personBek}/contracts/${person.memberContractId}`;
  }
}